import { useState } from "react";
import { Link2, Mail, Check } from "lucide-react";

// Canonical share URL — always points at production, even when previewing.
const SITE_URL = "https://truth4parents.com";

// Inline SVGs for platform brand marks (lucide's brand icons were deprecated).
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const BlueskyIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden {...props}>
    <path d="M12 10.9c-1.5-2.9-5.6-8.2-9.4-10.9-1.6-1.1-2.6-.4-2.6.9v9.9c0 1 .8 1.8 1.8 1.9 1.9.2 4.1.7 4.9 3.4-1.4-.4-3-1.1-4.9-2.4C1.6 15.1 0 15.7 0 17.1v.1c0 3.4 3.3 6 6.8 6 3 0 5.2-1.9 5.2-3.6 0 1.7 2.2 3.6 5.2 3.6 3.5 0 6.8-2.6 6.8-6v-.1c0-1.4-1.6-2-1.8-2.4-1.9 1.3-3.5 2-4.9 2.4.8-2.7 3-3.2 4.9-3.4 1-.1 1.8-.9 1.8-1.9V.9c0-1.3-1-2-2.6-.9-3.8 2.7-7.9 8-9.4 10.9z" />
  </svg>
);

const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const LinkedInIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14" aria-hidden {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export default function SocialShare({ title, slug }) {
  const url = `${SITE_URL}/journal/${slug}`;
  const encoded = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const [copied, setCopied] = useState(false);

  const shares = [
    {
      key: "x",
      label: "Post on X",
      href: `https://twitter.com/intent/tweet?url=${encoded}&text=${encodedTitle}`,
      Icon: XIcon,
    },
    {
      key: "bluesky",
      label: "Post on Bluesky",
      href: `https://bsky.app/intent/compose?text=${encodedTitle}%20${encoded}`,
      Icon: BlueskyIcon,
    },
    {
      key: "facebook",
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}`,
      Icon: FacebookIcon,
    },
    {
      key: "linkedin",
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`,
      Icon: LinkedInIcon,
    },
    {
      key: "email",
      label: "Email a friend",
      href: `mailto:?subject=${encodedTitle}&body=${encoded}`,
      Icon: Mail,
    },
  ];

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };

  return (
    <div
      data-testid="social-share"
      className="mt-16 pt-10 border-t border-rule max-w-3xl"
    >
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
        If this helped, pass it on
      </div>
      <p className="mt-3 text-sm text-ink-muted">
        This work grows by being read. Sharing is how families who need this find it.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {shares.map(({ key, label, href, Icon }) => (
          <a
            key={key}
            href={href}
            target={key === "email" ? "_self" : "_blank"}
            rel="noopener noreferrer"
            data-testid={`share-${key}-link`}
            aria-label={label}
            className="inline-flex items-center gap-2 border border-rule text-ink-muted hover:border-ink hover:text-ink px-4 py-2 text-xs font-accent font-semibold transition-colors"
          >
            <Icon />
            {label}
          </a>
        ))}
        <button
          type="button"
          onClick={onCopy}
          data-testid="share-copy-button"
          className="inline-flex items-center gap-2 border border-rule text-ink-muted hover:border-ink hover:text-ink px-4 py-2 text-xs font-accent font-semibold transition-colors"
        >
          {copied ? (
            <>
              <Check size={14} strokeWidth={1.5} />
              Link copied
            </>
          ) : (
            <>
              <Link2 size={14} strokeWidth={1.5} />
              Copy link
            </>
          )}
        </button>
      </div>
    </div>
  );
}
